// Curriculum Service - API'den müfredat verisi çekme
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache için
let levelsCache = { english: null, cabin_crew: null };
let lessonContentsCache = {};

/**
 * Tüm seviyeleri API'den çek (İngilizce veya Kabin Memurluğu)
 */
export async function fetchAllLevels(educationType = 'english') {
  // Cache kontrolü
  if (levelsCache[educationType]) {
    return levelsCache[educationType];
  }

  try {
    const res = await axios.get(`${API}/curriculum/levels?education_type=${educationType}`, { 
      withCredentials: true 
    });
    
    if (res.data.levels && res.data.levels.length > 0) {
      // API'den gelen veriyi formatla
      const levels = res.data.levels.map((level, idx) => ({
        ...level,
        id: level.id || `level_${idx}`,
        level_code: level.level_code || `L${idx}`,
        title: level.title || 'Untitled',
        titleTr: level.titleTr || level.subtitle || '',
        description: level.description || '',
        color: level.color || 'from-blue-400 to-blue-600',
        icon: level.icon || '📚',
        units: (level.units || []).map(unit => ({
          ...unit,
          id: unit.id || unit.unit_id,
          unit_id: unit.unit_id || unit.id,
          lessons: (unit.lessons || []).map(lesson => ({
            ...lesson,
            id: lesson.id || lesson.lesson_id,
            lesson_id: lesson.lesson_id || lesson.id
          }))
        }))
      }));
      
      levelsCache[educationType] = levels;
      return levels;
    }
    return [];
  } catch (error) {
    console.error('Error fetching levels:', error);
    return [];
  }
}

/**
 * Belirli bir seviyeyi API'den çek
 */
export async function fetchLevel(levelId, educationType = 'english') {
  try {
    // Önce cache'den bak
    if (levelsCache[educationType]) {
      const cached = levelsCache[educationType].find(l => l.id === levelId);
      if (cached) return cached;
    }
    
    // API'den çek
    const res = await axios.get(`${API}/curriculum/level/${levelId}?education_type=${educationType}`, {
      withCredentials: true
    });
    
    if (res.data.level) {
      const level = res.data.level;
      return {
        ...level,
        id: level.id || levelId,
        units: (level.units || []).map(unit => ({
          ...unit,
          id: unit.id || unit.unit_id,
          unit_id: unit.unit_id || unit.id,
          lessons: (unit.lessons || []).map(lesson => ({
            ...lesson,
            id: lesson.id || lesson.lesson_id,
            lesson_id: lesson.lesson_id || lesson.id
          }))
        }))
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching level:', error);
    return null;
  }
}

/**
 * Ders içeriğini API'den çek
 */
export async function fetchLessonContent(levelId, unitId, lessonId, educationType = "cabin_crew") {
  const cacheKey = `${levelId}-${unitId}-${lessonId}`;
  
  // Cache kontrolü
  if (lessonContentsCache[cacheKey]) {
    return lessonContentsCache[cacheKey];
  }

  try {
    const res = await axios.get(
      `${API}/curriculum/lesson-content/${levelId}/${unitId}/${lessonId}?education_type=${educationType}`,
      { withCredentials: true }
    );
    
    if (res.data.content) {
      lessonContentsCache[cacheKey] = res.data.content;
      return res.data.content;
    }
    return null;
  } catch (error) {
    console.error('Error fetching lesson content:', error);
    return null;
  }
}

/**
 * Cache'i temizle (admin içerik güncellemelerinde kullanılır)
 */
export function clearCurriculumCache() {
  levelsCache = { english: null, cabin_crew: null };
  lessonContentsCache = {};
}

/**
 * Özel içerik API'si varsa kullanan yardımcı - yoksa fallback
 */
export async function getLessonContentWithFallback(levelId, unitId, lessonId, staticFallback) {
  const apiContent = await fetchLessonContent(levelId, unitId, lessonId);
  if (apiContent) {
    return apiContent;
  }
  // API'de yoksa static fallback kullan
  return staticFallback ? staticFallback(levelId, unitId, lessonId) : null;
}

export default {
  fetchAllLevels,
  fetchLevel,
  fetchLessonContent,
  clearCurriculumCache,
  getLessonContentWithFallback
};
